import { FC, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import CustomTabulation from "../../../custom-components/CustomTabulation";
import { CreateWorkTabsEnum } from "../../../domain/enums/CreateWorkTabsEnum";
import { useAppDispatch } from "../../../custom-hooks/useAppDispatch";
import useCustomFormik from "../../../custom-hooks/useCustomFormik";
import {
    workCreationFirstStepFields,
    workCreationFirstStepSchemaFactory,
    workCreationSecondStepFields,
    workCreationSecondStepSchemaFactory,
} from "../../../validation-schemas/work-schemas/WorkCreationSchema";
import { WorkServiceImpl } from "../../../services/WorkServiceImpl";
import CreateWorkFirstStep from "./CreateWorkFirstStep";
import CreateWorkSecondStep from "./CreateWorkSecondStep";
import { useSelector } from "react-redux";
import { selectUser } from "../../../slices/user-slice";
import phoneServiceFactory from "../../../services/PhoneServiceImpl";
import CreateWorkSuccess from "./CreateWorkSuccess";
import { WorkDTO } from "../../../domain/models/dto/WorkDTO";
import { InitialAddress } from "../../../domain/models/Address";
import _ from "lodash";
import { shouldApplyProfileContact } from "./CreateWorkContactPrefill";
import { PhoneInfoForm } from "../../../domain/models/dto/PhoneDTO";

const phoneService = phoneServiceFactory();

const CreateWorkCreationForm: FC = () => {
    const [workId, setWorkId] = useState("");
    const [tabName, setTabName] = useState(CreateWorkTabsEnum.STEP1);
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const user = useSelector(selectUser());
    const initialContacts = useRef({
        phones: user.phones?.map((phone) => ({ ...phone })),
        telegram: user.telegram,
    });
    const {
        form: firstStepForm,
        errorKey: firstStepErrorKey,
        setErrorKey: firstStepSetErrorKey,
    } = useCustomFormik({
        initial: { address: { ...InitialAddress, country: user.address?.country } },
        fields: workCreationFirstStepFields,
        schema: workCreationFirstStepSchemaFactory(t),
        onSubmit: async () => {
            setTabName(CreateWorkTabsEnum.STEP2);
        },
    });

    const {
        form: secondStepForm,
        errorKey: secondStepErrorKey,
        setErrorKey: secondStepSetErrorKey,
    } = useCustomFormik({
        initial: initialContacts.current,
        fields: workCreationSecondStepFields,
        schema: workCreationSecondStepSchemaFactory(t),
        onSubmit: async (values) => {
            if (values.phones.every((item) => !item.phone) && !values.telegram) {
                secondStepSetErrorKey("errors.contactsInfoIncomplete");
                return;
            }
            if (phoneService.checkDuplicates(values.phones)) {
                secondStepSetErrorKey("errors.response.custom.duplicatedPhones");
                return;
            }
            if (!values.expiresInMinutes && values.showContactsForAll) {
                secondStepSetErrorKey("workCreation.errors.expiresIn");
                return;
            }
            const work = await dispatch(WorkServiceImpl.createWorkThunk({
                ...values,
                ...firstStepForm.values,
                telegram: values.telegram
                    ? values.telegram
                        .replace(/^(https?:\/\/)?(t\.me\/|telegram\.me\/)/i, "")
                        .replace(/^@/, "")
                        .replace(/[^a-zA-Z0-9_]/g, "")
                        .slice(0, 32) || null
                    : values.telegram,
            }));
            if (work.meta.requestStatus !== "fulfilled") {
                secondStepSetErrorKey("errors.common");
                return;
            }
            setWorkId((work.payload as WorkDTO)?.id || "");
        },
    });
    const editedContacts = useRef({ phones: false, telegram: false });
    const appliedProfileContacts = useRef<{
        profileId: string;
        phones: PhoneInfoForm[] | undefined;
        telegram: string | undefined;
    }>({
        profileId: user.id,
        phones: secondStepForm.values.phones,
        telegram: secondStepForm.values.telegram,
    });
    const previousFormContacts = useRef({
        phones: secondStepForm.values.phones,
        telegram: secondStepForm.values.telegram,
    });

    useEffect(() => {
        if (!_.isEqual(previousFormContacts.current.phones, secondStepForm.values.phones)) {
            editedContacts.current.phones = true;
        }
        if (previousFormContacts.current.telegram !== secondStepForm.values.telegram) {
            editedContacts.current.telegram = true;
        }

        previousFormContacts.current = {
            phones: secondStepForm.values.phones,
            telegram: secondStepForm.values.telegram,
        };
    }, [secondStepForm.values.phones, secondStepForm.values.telegram]);

    useEffect(() => {
        if (!user.id) {
            return;
        }

        if (appliedProfileContacts.current.profileId !== user.id) {
            appliedProfileContacts.current = {
                profileId: user.id,
                phones: secondStepForm.values.phones,
                telegram: secondStepForm.values.telegram,
            };
        }

        const profilePhones = user.phones?.map((phone) => ({ ...phone }));

        if (
            profilePhones?.length &&
            shouldApplyProfileContact(
                profilePhones,
                appliedProfileContacts.current.phones,
                secondStepForm.values.phones,
                editedContacts.current.phones,
                !!secondStepForm.touched.phones
            )
        ) {
            appliedProfileContacts.current.phones = profilePhones;
            previousFormContacts.current.phones = profilePhones;
            secondStepForm.setFieldValue("phones", profilePhones, false);
        }

        if (
            shouldApplyProfileContact(
                user.telegram,
                appliedProfileContacts.current.telegram,
                secondStepForm.values.telegram,
                editedContacts.current.telegram,
                !!secondStepForm.touched.telegram
            )
        ) {
            const profileTelegram = user.telegram || "";
            appliedProfileContacts.current.telegram = profileTelegram;
            previousFormContacts.current.telegram = profileTelegram;
            secondStepForm.setFieldValue("telegram", profileTelegram, false);
        }
    }, [user.id, user.phones, user.telegram, secondStepForm]);

    const tabSwitchHandler = (tab: CreateWorkTabsEnum) => {
        if (firstStepForm.isValid && firstStepForm.dirty) {
            firstStepSetErrorKey("");
            secondStepSetErrorKey("");
            setTabName(tab);
        } else {
            firstStepSetErrorKey("errors.validation.fillInTheForm");
        }
    };

    return (
        <div className="mx-auto dmin2:w-[1280px] di6:w-[800px] ti6:w-[650px] ti7:w-[560px] tmax4:w-[360px]">
            <h1 className="H32B dmax2:H24B text-textWhite mb-9 dmax2:mb-6 dmin2:mt-5">{t("workCreation.title")}</h1>
            <div className="flex flex-col bg-backgroundBlock rounded-lg dmax2:rounded-md px-[44px] td4:px-5 tmax4:px-[15px] py-[52px] dmax2:py-8 w-full mb-[80px]">
                <div className="w-full h-full">
                    <CustomTabulation
                        tabsEnum={CreateWorkTabsEnum}
                        tabName={tabName}
                        tabSwitchHandler={tabSwitchHandler}
                        tabTitleKey="registration"
                        wrapClassName="mb-5 dmax2:mb-3 text-center"
                        unorderedListClassName="gap-5"
                        listItemClassName="flex items-center"
                        tabNameActiveClassName="H24B dmax2:H20B text-textWhite dmin2:px-5 pb-3 dmax2:pb-2 border-b-4 dmax2:border-b-3 border-textWarning"
                        tabNameClassName="H20B dmax2:H16B text-textFaded dmin2:px-5 pb-3 dmax2:pb-3"
                    />
                </div>
                {tabName === CreateWorkTabsEnum.STEP1 ? (
                    <>
                        <p className="H20 dmax2:H14 text-textFaded mb-9 dmax2:mb-6">
                            {t("workCreation.descriptionFirstStep")}
                        </p>
                        <CreateWorkFirstStep form={firstStepForm} errorKey={firstStepErrorKey} />
                    </>
                ) : (
                    <>
                        <p className="H20 dmax2:H14 text-textFaded mb-9 dmax2:mb-6">
                            {t("workCreation.descriptionSecondStep")}
                        </p>
                        <CreateWorkSecondStep form={secondStepForm} errorKey={secondStepErrorKey} />
                    </>
                )}
            </div>
            <CreateWorkSuccess workId={workId} resetWorkId={() => setWorkId("")} />
        </div>
    );
};

export default CreateWorkCreationForm;
